// apps/merchant-v3/src/services/walletClient.ts
//
// Phase 6 Wave 9 Task 28 — Thin wrapper over `apiClient` for the
// merchant Wallet overview surface. Read-only endpoints fetched by
// the WalletOverviewPage, KPI grid, Credit section and Spending
// chart. The mutate-side endpoints (topup / withdraw / send /
// transactions list) land in Tasks 29 / 30 / 31 and are intentionally
// stubbed here as `// TODO(task-29..31)` so this client stays small
// and Task 28's typecheck stays scoped.
//
//   GET  /api/v1/wallet/balance              — current balance + currency
//   GET  /api/v1/wallet/credit               — credit limit / used / available
//   GET  /api/v1/wallet/spending-summary     — daily category breakdown
//   GET  /api/v1/wallet/kpi-sparklines       — 7d sparkline data per KPI
//
// Decisions covered:
//    (5 KPI cards), (Credit section layout)
//   /73/74/75 (Charge flow logic + block-when-exhausted)
//    (Debt warning thresholds), (WS live refresh)
//    (Low-balance threshold logic), (Spending chart
//   category breakdown — 9 types union).
//
// Phase 6 Wave 9 Task 30 — appended Transactions ledger + Send /
// Withdraw / Transfer / 2FA helpers:
//   GET  /wallet/transactions                     — paginated 9-type ledger
//   GET  /wallet/transactions/:id/receipt.pdf     — receipt blob
//   GET  /wallet/transactions/export.{csv|xlsx}   — bulk export blob
//   GET  /wallet/lookup-recipient                 — email → user lookup
//                                                    (step 2)
//   POST /wallet/send                             — Send Balance with
//                                                    `Idempotency-Key`
//                                                    (step 4)
//   GET  /wallet/bank-accounts                    — merchant verified
//                                                    withdrawal destinations
//   POST /wallet/withdraw                         — request a withdrawal
//   POST /wallet/transfer                         — between own linked
//                                                    accounts
//   POST /auth/2fa/verify                         — security challenge
//                                                    re-used by Send +
//                                                    Withdraw + Transfer
//
// Phase 6 Wave 9 Task 29 — appended Topup mutate-side helpers:
//   POST /wallet/topup/instant         — kick off instant topup; returns
//                                        gateway-specific handoff
//                                        (PayPal approveUrl / Stripe
//                                        clientSecret / generic sessionUrl)
//   POST /wallet/topup/manual          — pre-payment invoice + payee
//                                        details for manual transfer
//   POST /wallet/topup/manual/:id/upload-proof — FormData proof upload
//   GET  /wallet/topups                — paginated history list
//   POST /wallet/topups/:id/cancel     — merchant-initiated cancel
//   POST /wallet/topup/:gateway/retry  — retry a failed instant topup
//   projectTopupConfig()               — adapts the flat
//                                        admin-content namespace into
//                                        the richer 3-segment shape
//                                        (instant / manual / fees)
//                                        consumed by the Topup page.
//
// Phase 6 Wave 9 Task 31 — appended Linked accounts + Bank accounts
// CRUD helpers consumed by the new
// `WalletLinkedAccountsPage` / `WalletBankAccountsPage` surfaces:
//   GET    /wallet/linked-accounts            — list linked merchants
//   POST   /wallet/linked-accounts            — link by recipientEmail
//   DELETE /wallet/linked-accounts/:id        — unlink
//   GET    /wallet/bank-accounts              — already implemented in
//                                               Task 30; reused as-is
//   POST   /wallet/bank-accounts              — add a bank account
//                                               (X-2FA-Token header)
//   DELETE /wallet/bank-accounts/:id          — remove a bank account
//
// vg-binding: PLAN:task-28
// vg-binding: PLAN:task-29
// vg-binding: PLAN:task-30
// vg-binding: PLAN:task-31
// vg-binding: P6.D-65
// vg-binding: P6.D-69
// vg-binding: P6.D-233
// vg-binding: G-49
// vg-binding: G-50
// vg-binding: G-51
// vg-binding: P6.D-64
// vg-binding: P6.D-66
// vg-binding: P6.D-67
// vg-binding: P6.D-68
// vg-binding: P6.D-71
// vg-binding: P6.D-72
// vg-binding: P6.D-73
// vg-binding: P6.D-74
// vg-binding: P6.D-75
// vg-binding: P6.D-76
// vg-binding: P6.D-77
// vg-binding: P6.D-78
// vg-binding: P6.D-79
// vg-binding: P6.D-234
// vg-binding: P6.D-244
// vg-binding: G-46
// vg-binding: G-47
// vg-binding: G-48
// vg-binding: INTERFACE-STANDARDS:response-envelope
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Phase 3.3 Wave 4 Task 07 — Amendment #1 grouped Transaction model
// . The transactions list now also projects the grouped
// envelope shape `{ id, type, totalCents, currency, orderCount,
// createdAt, expandable }`; `listTransactionOrders(transactionId)`
// lazy-fetches the per-order `OrderTransaction` children for the
// expandable row.
//
//   GET /api/v1/merchants/me/wallet/transactions/:id/orders
//                                     — per-order children of a batch
//
// Phase 3.3 REVIEW-FEEDBACK item 2 — contract alignment. The BE query
// schema is `.strict()` and only accepts `type` (comma-separated
// TxGroupType tokens) / `from`/`to` (epoch ms) / `gateway` / `cursor`
// (base64) / `limit` (1..200). `listTransactionGroups` now sends exactly
// that (`TransactionGroupsQuery`) and returns the real cursor page
// `{ rows, hasMore, nextCursor }`. sort/dir/q/page/pageSize DO NOT exist
// server-side — sort flip + note search are client-side in the grouped
// history component; pagination is cursor-stack based.
// Error path stays the toast-via-INTERFACE-STANDARDS priority owned by
// the caller (the grouped history orchestrator).
//
// vg-binding: CONTEXT:decisions
// vg-binding: INTERFACE-STANDARDS:error-shape

import { apiClient, ApiError, request } from '@/lib/api/client';
import type {
  BankAccount,
  LinkedAccount,
  LookupRecipientResponse,
  TransferRecipient,
  SendBalancePayload,
  SendBalanceResponse,
  TopupGatewayConfig as PageTopupGatewayConfig,
  TopupGatewayFee,
  Topup,
  TopupGateway,
  TopupInstantPayload,
  TopupInstantResponse,
  TopupListFilters,
  TopupListResult,
  TopupManualCreatePayload,
  TopupManualCreateResponse,
  TopupMode,
  OrderTransaction,
  Transaction,
  TransactionOrdersResult,
  TransferPayload,
  TransferResponse,
  Verify2FAResponse,
  WalletTransactionsFilters,
  WalletTransactionsResult,
  WalletTxExportFormat,
  WalletTxType,
  WithdrawPayload,
  WithdrawResponse,
} from '@/types/wallet';
import type { TopupGatewayConfig as AdminContentTopupConfig } from '@/types/admin-content';

// --- domain types ---------------------------------------------------------

/** GET /api/v1/wallet/balance — current spendable balance. Amount is in
 *  USD (NOT cents) to match the BE's `wallet.balance` decimal contract;
 *  formatting is handled in the UI via `Intl.NumberFormat`. */
export interface WalletBalance {
  /** Spendable balance in major currency units (USD). */
  balance: number;
  /** ISO-4217 currency code — currently always 'USD' in P6 scope. */
  currency: string;
  /** ISO-8601 timestamp of the last balance change (used as a cache
   *  busting hint + a "Last updated …" footnote in the UI). */
  lastUpdated: string;
  /** Optional pending topups count — surfaced in the KPI card when > 0. */
  pendingTopups?: number;
}

/** GET /api/v1/wallet/credit — credit envelope. `available` is
 *  the BE-computed `limit - used`; we keep it explicit so the FE never
 *  needs to re-derive (avoids float drift). */
export interface WalletCredit {
  limit: number;
  /** Outstanding debt — NỢ HIỆN TẠI (giảm khi trả/sweep). */
  used: number;
  available: number;
  /** ISO-8601 expiry date — when missing, credit never expires. */
  expiryDate?: string;
  /**
   * TỔNG đã dùng tích luỹ (lifetime, monotonic — không giảm khi trả nợ).
   * Tách khỏi `used` (nợ hiện tại). P3.D-21b.
   */
  lifetimeUsed?: number;
}

/** Spending category — 9 transaction types.
 *  Kept in sync with the BE enum at apps/api/src/modules/wallet. */
export type SpendingCategory =
  | 'orderCharge'
  | 'topup'
  | 'refund'
  | 'creditCharge'
  | 'creditRepay'
  | 'sendOut'
  | 'receiveIn'
  | 'cashback'
  | 'gatewayFee';

export const SPENDING_CATEGORIES: SpendingCategory[] = [
  'orderCharge',
  'topup',
  'refund',
  'creditCharge',
  'creditRepay',
  'sendOut',
  'receiveIn',
  'cashback',
  'gatewayFee',
];

/** Per-day breakdown row used by the SpendingChart. `byCategory` is a
 *  partial — categories with `0` are omitted by the BE so the BarChart
 *  silently skips empty stacks. */
export interface SpendingDailyRow {
  /** ISO-8601 calendar date (YYYY-MM-DD). */
  date: string;
  /** Net spent across all categories on this day, USD. */
  amount: number;
  /** Per-category breakdown — keys are `SpendingCategory` strings. */
  byCategory: Partial<Record<SpendingCategory, number>>;
}

export interface WalletSpendingSummary {
  totalSpent: number;
  /** Daily rows, oldest → newest, exactly `rangeDays` entries (the BE
   *  zero-fills empty days so the chart x-axis is dense). */
  daily: SpendingDailyRow[];
  averageDaily: number;
}

/** Sparkline point — `value` in USD, `date` is ISO-8601 calendar date. */
export interface SparklinePoint {
  date: string;
  value: number;
}

/** GET /api/v1/wallet/kpi-sparklines — 7-day trend per KPI card. Keys
 *  are the KPI ids consumed by `WalletKPI`. */
export interface WalletKPISparklines {
  balance: SparklinePoint[];
  creditUsed: SparklinePoint[];
  pendingTopups: SparklinePoint[];
  monthlySpending: SparklinePoint[];
}

// --- API helpers ----------------------------------------------------------

const BASE = '/api/v1/wallet';

/** Canonical merchant-scoped wallet base (phase 3.3). The grouped
 *  transaction history + per-batch order drill-down live HERE
 *  (GET /api/v1/merchants/me/wallet/transactions[/:id/orders]) — NOT
 *  under the legacy `/api/v1/wallet` base above. Hitting the legacy
 *  `/api/v1/wallet/transactions` returns flat WalletTransaction rows
 *  (no totalCents/orderCount → every grouped row renders $0.00). */
const ME_WALLET_BASE = '/api/v1/merchants/me/wallet';

/**
 * GET /api/v1/wallet/balance — spendable balance + currency. The BE may
 * return either `{ balance, currency, lastUpdated }` directly or wrap
 * the row under `{ wallet: { ... } }` for legacy parity; we accept both.
 * Falls back to a zero-balance shape on 404 so the page renders cleanly
 * for a brand-new merchant before the wallet row is materialized.
 */
export async function getWalletBalance(): Promise<WalletBalance> {
  try {
    const data = await apiClient.get<WalletBalance | { wallet?: WalletBalance }>(
      `${BASE}/balance`,
    );
    if (data && typeof data === 'object' && 'wallet' in data && data.wallet) {
      return data.wallet;
    }
    return data as WalletBalance;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return {
        balance: 0,
        currency: 'USD',
        lastUpdated: new Date().toISOString(),
        pendingTopups: 0,
      };
    }
    throw err;
  }
}

/**
 * GET /api/v1/wallet/credit — credit envelope. 404-fallback
 * returns a zero-credit shape so the section renders an empty / locked
 * state for merchants who have not yet been granted credit.
 */
export async function getWalletCredit(): Promise<WalletCredit> {
  try {
    const data = await apiClient.get<WalletCredit | { credit?: WalletCredit }>(
      `${BASE}/credit`,
    );
    if (data && typeof data === 'object' && 'credit' in data && data.credit) {
      return data.credit;
    }
    return data as WalletCredit;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { limit: 0, used: 0, available: 0 };
    }
    throw err;
  }
}

/**
 * GET /api/v1/wallet/spending-summary?days=30 — daily category
 * breakdown. Used by `SpendingChart` (Bar = stacked categories + Line =
 * trend) and by the LowBalanceAlert threshold derivation (
 * needs `averageDaily` for the "<1 day runway" rule).
 */
export async function getSpendingSummary(
  rangeDays: number,
): Promise<WalletSpendingSummary> {
  const days = Math.max(1, Math.min(365, Math.floor(rangeDays)));
  try {
    const data = await apiClient.get<
      WalletSpendingSummary | { summary?: WalletSpendingSummary }
    >(`${BASE}/spending-summary?days=${days}`);
    if (data && typeof data === 'object' && 'summary' in data && data.summary) {
      return data.summary;
    }
    return data as WalletSpendingSummary;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { totalSpent: 0, daily: [], averageDaily: 0 };
    }
    throw err;
  }
}

/**
 * GET /api/v1/wallet/kpi-sparklines — small 7-day trend datasets used
 * by the 5 KPI cards. Falls back to deriving from the spending summary
 * when the dedicated endpoint is missing (404) so the page never hard-
 * fails — sparklines will simply render flat lines.
 */
export async function getKPISparklines(): Promise<WalletKPISparklines> {
  try {
    const data = await apiClient.get<
      WalletKPISparklines | { sparklines?: WalletKPISparklines }
    >(`${BASE}/kpi-sparklines`);
    if (
      data &&
      typeof data === 'object' &&
      'sparklines' in data &&
      data.sparklines
    ) {
      return data.sparklines;
    }
    return data as WalletKPISparklines;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      const empty: SparklinePoint[] = [];
      return {
        balance: empty,
        creditUsed: empty,
        pendingTopups: empty,
        monthlySpending: empty,
      };
    }
    throw err;
  }
}

/**
 * Derive sparkline points for the `monthlySpending` KPI from a
 * `WalletSpendingSummary`. Used by `useKPISparklines()` as a fallback
 * when the dedicated endpoint is unavailable. Exported so unit tests
 * can exercise the projection without going through React Query.
 */
export function deriveMonthlySpendingSparkline(
  summary: WalletSpendingSummary | undefined,
): SparklinePoint[] {
  if (!summary || summary.daily.length === 0) return [];
  // Take the trailing 7 days (or all available) for the sparkline.
  const tail = summary.daily.slice(-7);
  return tail.map((d) => ({ date: d.date, value: d.amount }));
}

// --- Task 29: Topup mutate-side helpers ----------------------------------

/**
 * POST /api/v1/wallet/topup/instant — kick off an instant gateway
 * topup. The BE returns a gateway-specific handoff:
 *   - PayPal     → { topupId, orderId, approveUrl }
 *   - Stripe     → { topupId, clientSecret, sessionUrl? }
 *   - Payoneer / LianLian Global → { topupId, sessionUrl }
 * Caller branches on which fields are populated. (.)
 */
export async function topupInstant(
  payload: TopupInstantPayload,
): Promise<TopupInstantResponse> {
  return apiClient.post<TopupInstantResponse>(
    `${BASE}/topup/instant`,
    payload,
  );
}

/**
 * POST /api/v1/wallet/topup/manual — open a manual topup, returning
 * the pre-payment invoice PDF + payee details for the merchant to
 * make the bank transfer. The merchant later uploads proof via
 * `uploadTopupProof`. (step 2.)
 */
export async function topupManualCreate(
  payload: TopupManualCreatePayload,
): Promise<TopupManualCreateResponse> {
  return apiClient.post<TopupManualCreateResponse>(
    `${BASE}/topup/manual`,
    payload,
  );
}

/**
 * POST /api/v1/wallet/topup/manual/:id/upload-proof — FormData upload
 * of the bank-transfer proof (PDF / PNG / JPG). On success the topup
 * row transitions to `processing` (admin review). (step 3.)
 */
export async function uploadTopupProof(
  topupId: string,
  file: File,
): Promise<{ ok: true }> {
  const fd = new FormData();
  fd.append('proof', file);
  return apiClient.post<{ ok: true }>(
    `${BASE}/topup/manual/${encodeURIComponent(topupId)}/upload-proof`,
    fd,
  );
}

/**
 * GET /api/v1/wallet/topups — paginated history list. Filters are
 * forwarded as query params; the BE applies sensible defaults
 * (createdAt DESC, page=1, pageSize=25). 404-fallback returns an
 * empty page so first-paint never hard-fails. (.)
 */
export async function listTopups(
  filters: TopupListFilters = {},
): Promise<TopupListResult> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.mode) qs.set('mode', filters.mode);
  if (filters.gateway) qs.set('gateway', filters.gateway);
  if (filters.fromDate) qs.set('fromDate', filters.fromDate);
  if (filters.toDate) qs.set('toDate', filters.toDate);
  if (filters.page) qs.set('page', String(filters.page));
  if (filters.pageSize) qs.set('pageSize', String(filters.pageSize));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  try {
    const data = await apiClient.get<
      TopupListResult | { topups?: Topup[]; total?: number }
    >(`${BASE}/topups${suffix}`);
    if (data && typeof data === 'object' && 'rows' in data && Array.isArray(data.rows)) {
      return data as TopupListResult;
    }
    if (data && typeof data === 'object' && 'topups' in data && Array.isArray(data.topups)) {
      return { rows: data.topups as Topup[], total: (data.total as number) ?? data.topups.length };
    }
    return { rows: [], total: 0 };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.code === 'NETWORK_ERROR')) {
      return { rows: [], total: 0 };
    }
    throw err;
  }
}

/**
 * POST /api/v1/wallet/topups/:id/cancel — merchant cancels a pending
 * topup. The BE flips the row to `cancelled` and emits the
 * `WALLET_BALANCE_CHANGED` WS push when the cancel reverses any
 * provisional credit. (.)
 */
export async function cancelTopup(id: string): Promise<{ ok: true }> {
  return apiClient.post<{ ok: true }>(
    `${BASE}/topups/${encodeURIComponent(id)}/cancel`,
    {},
  );
}

/**
 * POST /api/v1/wallet/topup/:gateway/retry — retry a failed instant
 * topup. The BE re-creates the gateway session and returns the same
 * handoff shape as `topupInstant()` for the original gateway.
 */
export async function retryTopup(
  gateway: TopupGateway,
): Promise<TopupInstantResponse> {
  return apiClient.post<TopupInstantResponse>(
    `${BASE}/topup/${encodeURIComponent(gateway)}/retry`,
    {},
  );
}

// --- Task 29: TopupGatewayConfig adapter ---------------------------------

/** Map the flat admin-content `topup-gateway-config` shape (Task 05)
 *  into the richer `{ instant, manual, fees }` view consumed by the
 *  Wallet Topup page. Dual-mode gateways (mode === 'dual') populate
 *  BOTH the `instant` and `manual` enabled maps and write a `both`
 *  fee entry; the page can refine that with a `:instant` / `:manual`
 *  composite key when the planner provides distinct rates per mode
 *  . */
export function projectTopupConfig(
  raw: AdminContentTopupConfig,
): PageTopupGatewayConfig {
  const instant: Record<string, { enabled: boolean }> = {};
  const manual: Record<string, { enabled: boolean }> = {};
  const fees: Record<string, TopupGatewayFee> = {};

  for (const gw of raw.gateways ?? []) {
    if (gw.mode === 'instant' || gw.mode === 'dual') {
      instant[gw.id] = { enabled: gw.enabled };
    }
    if (gw.mode === 'manual' || gw.mode === 'dual') {
      manual[gw.id] = { enabled: gw.enabled };
    }
    const feeMode: TopupMode | 'both' =
      gw.mode === 'dual' ? 'both' : (gw.mode as TopupMode);
    fees[gw.id] = {
      mode: feeMode,
      ratePct: gw.feePercent,
    };
    // independent fee per mode for dual-mode gateways.
    // Default to the same rate (planner-overrides land in P8 admin-
    // content CRUD); we still seed both keys so DualModeToggle can
    // read `fees['<id>:instant']` / `fees['<id>:manual']` directly.
    if (gw.mode === 'dual') {
      fees[`${gw.id}:instant`] = { mode: 'instant', ratePct: gw.feePercent };
      fees[`${gw.id}:manual`] = { mode: 'manual', ratePct: gw.feePercent };
    }
  }

  return { instant, manual, fees };
}

// --- Task 30: Transactions ledger ---------------------------------------

/**
 * GET /api/v1/wallet/transactions — paginated, filterable ledger of the
 * 9 transaction types (Gateway Fee). 404 / network
 * fallback returns an empty page so first paint never hard-fails.
 */
export async function listTransactions(
  filters: WalletTransactionsFilters = {},
): Promise<WalletTransactionsResult> {
  const qs = new URLSearchParams();
  if (filters.type) qs.set('type', filters.type);
  if (filters.status) qs.set('status', filters.status);
  if (filters.fromDate) qs.set('fromDate', filters.fromDate);
  if (filters.toDate) qs.set('toDate', filters.toDate);
  if (filters.page) qs.set('page', String(filters.page));
  if (filters.pageSize) qs.set('pageSize', String(filters.pageSize));
  if (filters.txNo) qs.set('txNo', filters.txNo);
  if (filters.method) qs.set('method', filters.method);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  try {
    const data = await apiClient.get<
      | WalletTransactionsResult
      | { transactions?: WalletTransactionsResult['rows']; total?: number }
    >(`${BASE}/transactions${suffix}`);
    if (
      data &&
      typeof data === 'object' &&
      'rows' in data &&
      Array.isArray((data as WalletTransactionsResult).rows)
    ) {
      return data as WalletTransactionsResult;
    }
    if (
      data &&
      typeof data === 'object' &&
      'transactions' in data &&
      Array.isArray((data as { transactions?: unknown[] }).transactions)
    ) {
      const d = data as {
        transactions: WalletTransactionsResult['rows'];
        total?: number;
      };
      return { rows: d.transactions, total: d.total ?? d.transactions.length };
    }
    return { rows: [], total: 0 };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [], total: 0 };
    }
    throw err;
  }
}

// --- Task 07 (Phase 3.3): grouped Transaction model -----------

/**
 * Query params for GET /me/wallet/transactions — mirrors the BE Zod
 * `TxQuerySchema` (`.strict()`, wallet.handler.ts) EXACTLY: only `type`
 * (comma-separated `TxGroupType` tokens), `from`/`to` (unix epoch ms),
 * `gateway`, `cursor` (base64 `{lastCreatedAt,lastId}`) and `limit`
 * (1..200, default 20) are accepted. Anything else (page/pageSize/sort/
 * dir/q/fromDate/toDate) is a 400 BILLING_INVALID_FILTER — the pre-3.3
 * client sent exactly those (REVIEW-FEEDBACK.md item 2). Sort is fixed
 * server-side (createdAt desc) and note-search does not exist on the BE;
 * both are client-side concerns now.
 *
 * vg-binding: CONTEXT:decisions
 */
export interface TransactionGroupsQuery {
  /** Comma-separated SERVER `TxGroupType` tokens (e.g.
   *  'TOP_UP,INSTANT_TOPUP') — use `WALLET_TX_TYPE_TO_SERVER` to map a
   *  FE `WalletTxType` before calling. */
  type?: string;
  /** Exact gateway slug match (tx_group.metadata.gateway). */
  gateway?: string;
  /** Inclusive lower bound on createdAt, unix epoch ms. */
  from?: number;
  /** Upper bound on createdAt, unix epoch ms. */
  to?: number;
  /** Base64 cursor from a prior page's `nextCursor`. */
  cursor?: string;
  /** Page size, 1..200 (server default 20). */
  limit?: number;
}

/** Cursor page envelope returned by the grouped/raw history feed
 *  (`{ rows, hasMore, nextCursor }` inside the `{success,data,error}`
 *  envelope that apiClient already unwraps). */
export interface TransactionGroupsPage {
  rows: Transaction[];
  hasMore: boolean;
  /** Base64 cursor for the next page, or null on the last page. */
  nextCursor: string | null;
}

/**
 * FE `WalletTxType` → server `TxGroupType` filter tokens. The server
 * enum is UPPER_SNAKE ('ORDER_CHARGE', 'TOP_UP', …) and finer-grained
 * than the FE 9-type union, so one FE type can fan out to several
 * tokens. `gateway_fee` has NO TxGroupType token — callers must not
 * offer it as a server filter (mapping intentionally absent).
 */
export const WALLET_TX_TYPE_TO_SERVER: Partial<
  Record<WalletTxType, readonly string[]>
> = {
  order_charge: ['ORDER_CHARGE', 'SURCHARGE_CHARGE'],
  topup: ['TOP_UP', 'INSTANT_TOPUP'],
  refund: ['ORDER_REFUND'],
  credit_charge: ['CREDIT_USAGE'],
  credit_repay: ['CREDIT_PAYMENT'],
  send_out: ['TRANSFER_SENT', 'WITHDRAW'],
  receive_in: ['TRANSFER_RECEIVED'],
  cashback: ['CASHBACK'],
};

/** Server `TxGroupType`/`TransactionType` → FE `WalletTxType`. Covers
 *  the grouped projection types (ORDER_CHARGE/ORDER_REFUND/
 *  SURCHARGE_CHARGE) plus the raw-feed baseline tokens the FE can
 *  request via `WALLET_TX_TYPE_TO_SERVER`. */
const SERVER_TX_TYPE_TO_WALLET: Record<string, WalletTxType> = {
  ORDER_CHARGE: 'order_charge',
  SURCHARGE_CHARGE: 'order_charge',
  EMBROIDERY_CHARGE: 'order_charge',
  CHARGEBACK_RECOVERY: 'order_charge',
  ORDER_REFUND: 'refund',
  TOP_UP: 'topup',
  INSTANT_TOPUP: 'topup',
  TRANSFER_SENT: 'send_out',
  WITHDRAW: 'send_out',
  TRANSFER_RECEIVED: 'receive_in',
  CREDIT_USAGE: 'credit_charge',
  CREDIT_PAYMENT: 'credit_repay',
  CASHBACK: 'cashback',
};

/** Number coercion that tolerates the BE sending `totalCents` /
 *  `amountCents` as either a number or a numeric string. */
function toNumber(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Normalize a createdAt/occurredAt value into an ISO-8601 string. The
 *  BE emits unix ms NUMBERS (TxGroupedRow.createdAt / TxHistoryRow
 *  .occurredAt); test stubs may emit ISO strings. Numeric strings are
 *  treated as unix ms too. */
function toIsoTimestamp(v: unknown): string {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return new Date(v).toISOString();
  }
  if (typeof v === 'string' && v !== '') {
    const asNum = Number(v);
    if (Number.isFinite(asNum)) return new Date(asNum).toISOString();
    return v;
  }
  return '';
}

/**
 * Normalize one raw history row into the FE `Transaction` shape. The BE
 * serves TWO row shapes on the same endpoint (wallet.handler.ts routing):
 *   • grouped `TxGroupedRow` — `{ id, type(ORDER_CHARGE|ORDER_REFUND|
 *     SURCHARGE_CHARGE), totalCents, currency, orderCount, createdAt(ms),
 *     expandable }` (default / order-related filters);
 *   • raw `TxHistoryRow` — `{ txId, type(TxGroupType), occurredAt(ms),
 *     gateway, amountCents(signed), balanceAfterCents, note, createdBy }`
 *     when the `type` filter is non-order-only (e.g. topups).
 * Both are folded into `Transaction`; raw rows get `orderCount=0` /
 * `expandable=false` and `totalCents=|amountCents|`.
 */
function parseTransaction(raw: Record<string, unknown>): Transaction {
  const orderCount = toNumber(raw.orderCount);
  const serverType = typeof raw.type === 'string' ? raw.type : '';
  const type =
    SERVER_TX_TYPE_TO_WALLET[serverType] ??
    // Already-snake_case FE value (test stubs) passes through untouched.
    (serverType as Transaction['type']);
  const totalCents =
    'totalCents' in raw
      ? toNumber(raw.totalCents)
      : Math.abs(toNumber(raw.amountCents));
  return {
    id: String(raw.id ?? raw.txId ?? ''),
    type,
    totalCents,
    currency: String(raw.currency ?? 'USD'),
    orderCount,
    createdAt: toIsoTimestamp(raw.createdAt ?? raw.occurredAt),
    expandable:
      typeof raw.expandable === 'boolean' ? raw.expandable : orderCount > 0,
    note: typeof raw.note === 'string' ? raw.note : null,
  };
}

/**
 * GET /api/v1/merchants/me/wallet/transactions — cursor-paginated
 * grouped batch ledger (contract verified by G-06). Each grouped row is
 * ONE pay/refund batch envelope carrying `totalCents` + `orderCount`;
 * expandable rows lazy-fetch children via `listTransactionOrders`.
 * Query params match the BE `.strict()` schema exactly (see
 * `TransactionGroupsQuery`). Returns `{ rows, hasMore, nextCursor }`;
 * also tolerates a legacy/stub `{ items }` array. 404 / network
 * fallback returns an empty page so first paint never hard-fails
 * (server 4xx/5xx still throw → error state).
 */
export async function listTransactionGroups(
  query: TransactionGroupsQuery = {},
): Promise<TransactionGroupsPage> {
  const qs = new URLSearchParams();
  if (query.type) qs.set('type', query.type);
  if (query.gateway) qs.set('gateway', query.gateway);
  if (query.from !== undefined) qs.set('from', String(query.from));
  if (query.to !== undefined) qs.set('to', String(query.to));
  if (query.cursor) qs.set('cursor', query.cursor);
  if (query.limit !== undefined) qs.set('limit', String(query.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  try {
    const data = await apiClient.get<unknown>(
      `${ME_WALLET_BASE}/transactions${suffix}`,
    );
    const obj = (data ?? {}) as Record<string, unknown>;
    const rawRows = Array.isArray(obj.rows)
      ? obj.rows
      : Array.isArray(obj.items)
        ? obj.items
        : [];
    const rows = (rawRows as unknown[]).map((r) =>
      parseTransaction((r ?? {}) as Record<string, unknown>),
    );
    return {
      rows,
      hasMore: obj.hasMore === true,
      nextCursor: typeof obj.nextCursor === 'string' ? obj.nextCursor : null,
    };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [], hasMore: false, nextCursor: null };
    }
    throw err;
  }
}

/**
 * GET /api/v1/merchants/me/wallet/transactions/:transactionId/orders —
 * per-order `OrderTransaction` children of a grouped batch.
 * Accepts the wave-3 `{ children: [...] }` envelope (also tolerates a
 * bare array for forward-compat). 404 / network fallback returns `[]`
 * so an empty expand never throws — the error path is reserved for the
 * caller's toast (INTERFACE-STANDARDS error-shape priority).
 */
export async function listTransactionOrders(
  transactionId: string,
): Promise<OrderTransaction[]> {
  const data = await apiClient.get<
    TransactionOrdersResult | OrderTransaction[]
  >(`${ME_WALLET_BASE}/transactions/${encodeURIComponent(transactionId)}/orders`);
  if (Array.isArray(data)) return data;
  if (
    data &&
    typeof data === 'object' &&
    'children' in data &&
    Array.isArray((data as TransactionOrdersResult).children)
  ) {
    return (data as TransactionOrdersResult).children;
  }
  return [];
}

/** Normalize one raw flat order-transaction row into the `OrderTransaction`
 *  shape. Tolerates `amountCents`/`createdAt` arriving as number or string. */
function parseOrderTransaction(
  raw: Record<string, unknown>,
): OrderTransaction {
  return {
    id: raw.id != null ? String(raw.id) : undefined,
    orderId: String(raw.orderId ?? ''),
    amountCents: toNumber(raw.amountCents),
    type: raw.type as OrderTransaction['type'],
    currency: raw.currency != null ? String(raw.currency) : undefined,
    parentTransactionId:
      raw.parentTransactionId != null
        ? String(raw.parentTransactionId)
        : undefined,
    createdAt:
      typeof raw.createdAt === 'number' || typeof raw.createdAt === 'string'
        ? raw.createdAt
        : undefined,
    // Enriched Order Transaction tab fields (debug 2026-06-15).
    orderCode: raw.orderCode != null ? String(raw.orderCode) : null,
    name: raw.name != null ? String(raw.name) : null,
    transactionNo:
      typeof raw.transactionNo === 'number' ? raw.transactionNo : null,
    surchargeCents: toNumber(raw.surchargeCents),
    method: raw.method != null ? String(raw.method) : null,
    note: raw.note != null ? String(raw.note) : null,
  };
}

/**
 * GET /api/v1/wallet/order-transactions — flat per-order
 * `OrderTransaction` feed (review #4). One row per order across
 * batches. Numbered (offset) pagination — `page` (1-based) + `pageSize` —
 * not cursor (debug 2026-06-15); the BE returns `{ rows, total }` so the
 * UI renders a 1,2,3,… `<Pagination>`. 404 / network fallback returns an
 * empty page so first paint never hard-fails.
 */
export async function listOrderTransactionsFlat(
  filters: {
    page?: number;
    pageSize?: number;
    q?: string;
    type?: string;
    fromDate?: string;
    toDate?: string;
    method?: string;
  } = {},
): Promise<{ rows: OrderTransaction[]; total: number }> {
  const qs = new URLSearchParams();
  if (filters.page) qs.set('page', String(filters.page));
  if (filters.pageSize) qs.set('pageSize', String(filters.pageSize));
  if (filters.q) qs.set('q', filters.q);
  if (filters.type) qs.set('type', filters.type);
  if (filters.fromDate) qs.set('fromDate', filters.fromDate);
  if (filters.toDate) qs.set('toDate', filters.toDate);
  if (filters.method) qs.set('method', filters.method);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  try {
    const data = await apiClient.get<unknown>(
      `${BASE}/order-transactions${suffix}`,
    );
    const obj = (data ?? {}) as Record<string, unknown>;
    const rawRows = Array.isArray(obj.rows) ? obj.rows : [];
    const rows = (rawRows as unknown[]).map((r) =>
      parseOrderTransaction((r ?? {}) as Record<string, unknown>),
    );
    const total = toNumber(obj.total) || rows.length;
    return { rows, total };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [], total: 0 };
    }
    throw err;
  }
}

/**
 * GET /api/v1/wallet/order-transactions/export.{csv|xlsx} — Order
 * Transactions (Export History) blob. Forwards the same filters as the
 * list so the file matches the visible tab (dbg-20260619, v2 parity).
 */
export async function exportOrderTransactions(
  format: WalletTxExportFormat,
  filters: {
    q?: string;
    type?: string;
    fromDate?: string;
    toDate?: string;
    method?: string;
  } = {},
): Promise<Blob> {
  const qs = new URLSearchParams();
  if (filters.q) qs.set('q', filters.q);
  if (filters.type) qs.set('type', filters.type);
  if (filters.fromDate) qs.set('fromDate', filters.fromDate);
  if (filters.toDate) qs.set('toDate', filters.toDate);
  if (filters.method) qs.set('method', filters.method);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await request<Response>(
    `${BASE}/order-transactions/export.${format}${suffix}`,
    { method: 'GET', raw: true },
  );
  if (!res.ok) {
    throw new ApiError(res.status, {
      ok: false,
      error: { code: 'EXPORT_FAILED', message: 'Export failed' },
    });
  }
  return res.blob();
}

/**
 * GET /api/v1/wallet/transactions/:id/receipt.pdf — receipt PDF as a
 * Blob. Uses the api-client `raw: true` escape hatch so the wrapper
 * skips JSON envelope parsing and we can await `Response.blob()`
 * directly. Auth + CSRF + 401-refresh still flow through `request()`.
 */
export async function getTransactionReceipt(id: string): Promise<Blob> {
  const res = await request<Response>(
    `${BASE}/transactions/${encodeURIComponent(id)}/receipt.pdf`,
    { method: 'GET', raw: true },
  );
  if (!res.ok) {
    throw new ApiError(res.status, {
      ok: false,
      error: {
        code: 'RECEIPT_DOWNLOAD_FAILED',
        message: 'Could not download receipt',
      },
    });
  }
  return res.blob();
}

/**
 * GET /api/v1/wallet/transactions/export.{csv|xlsx} — bulk export blob.
 * Filters are forwarded as query params identical to the list endpoint
 * so the exported file matches the table the user is currently looking
 * at (history view).
 */
export async function exportTransactions(
  format: WalletTxExportFormat,
  filters: WalletTransactionsFilters = {},
): Promise<Blob> {
  const qs = new URLSearchParams();
  if (filters.type) qs.set('type', filters.type);
  if (filters.status) qs.set('status', filters.status);
  if (filters.fromDate) qs.set('fromDate', filters.fromDate);
  if (filters.toDate) qs.set('toDate', filters.toDate);
  // method + txNo were dropped before (dbg-20260619) → export ignored the
  // active method/search filters and didn't match the visible table.
  if (filters.method) qs.set('method', filters.method);
  if (filters.txNo) qs.set('txNo', filters.txNo);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await request<Response>(
    `${BASE}/transactions/export.${format}${suffix}`,
    { method: 'GET', raw: true },
  );
  if (!res.ok) {
    throw new ApiError(res.status, {
      ok: false,
      error: {
        code: 'EXPORT_FAILED',
        message: 'Export failed',
      },
    });
  }
  return res.blob();
}

// --- Linked Accounts recipient lookup (NOT Send Balance) -----------------
//
// GET /api/v1/wallet/lookup-recipient?email=… — resolves a recipient email
// to a Printway user. STILL used by the Linked Accounts feature
// (AddLinkedAccountModal). Send Balance no longer uses this (moved to the
// Transfer Group recipient list below).
export async function lookupRecipient(
  email: string,
): Promise<LookupRecipientResponse> {
  const qs = new URLSearchParams({ email });
  return apiClient.get<LookupRecipientResponse>(
    `${BASE}/lookup-recipient?${qs.toString()}`,
  );
}

// --- Task 30: Send Balance (P6.D-69 amend#11 — Transfer Group) -----------
//
// Wires to the real, already-mounted transfer API (P3.D-19). Recipients are
// server-scoped to the sender's Transfer Groups — no free-form email lookup.

/** Transfer endpoints live at the apex `/api/v1/transfers`, NOT under the
 *  `/api/v1/wallet` BASE (those send/lookup paths never existed → 404). */
const TRANSFERS_BASE = '/api/v1/transfers';

/** Raw recipient row shape from GET /api/v1/transfers/recipients. */
interface RawTransferRecipient {
  merchant_id: string;
  name: string;
  available_balance: number | null;
}

/**
 * GET /api/v1/transfers/recipients — eligible transfer recipients, already
 * scoped server-side to the merchants in the sender's active Transfer
 * Groups (P3.D-19). Empty list ⇒ merchant belongs to no group ⇒ the
 * Send Balance entry point is hidden. 403/404/network → empty list so the
 * UI degrades to "no recipients" instead of crashing.
 */
export async function listTransferRecipients(): Promise<TransferRecipient[]> {
  try {
    const data = await apiClient.get<{ data?: RawTransferRecipient[] } | RawTransferRecipient[]>(
      `${TRANSFERS_BASE}/recipients`,
    );
    const rows = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
        ? data.data
        : [];
    return rows.map((r) => ({
      merchantId: r.merchant_id,
      name: r.name,
      availableBalanceCents: r.available_balance ?? null,
    }));
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.status === 403 || err.code === 'NETWORK_ERROR')
    ) {
      return [];
    }
    throw err;
  }
}

/** Generate a stable Idempotency-Key — `crypto.randomUUID` when
 *  available, fallback to a timestamp+random string. The key is sent
 *  on every Send / Withdraw mutation so a network retry never double-
 *  spends. */
function newIdempotencyKey(): string {
  // Avoid the eslint-disable-able `any` cast; deliberately read
  // crypto.randomUUID through a typed alias for env safety.
  const c = (
    globalThis as { crypto?: { randomUUID?: () => string } }
  ).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return (
    'idem_' + Math.random().toString(36).slice(2) + Date.now().toString(36)
  );
}

/** Raw success shape from POST /api/v1/transfers. */
interface RawTransferResponse {
  id: string;
  amount_usd_cents: number;
  sender_balance_after: number;
  recipient_balance_after: number;
  created_at: string;
}

/**
 * POST /api/v1/transfers — Send Balance to a merchant in the sender's
 * Transfer Group. The TOTP 2FA code is verified INLINE by this endpoint
 * (in the body), so there is no separate /auth/2fa/verify call.
 * Idempotency-Key prevents duplicate transfers on a retried POST. The BE
 * enforces group membership + per-member monthly limits server-side.
 */
export async function sendBalance(
  payload: SendBalancePayload,
  opts?: { idempotencyKey?: string },
): Promise<SendBalanceResponse> {
  const body: Record<string, unknown> = {
    recipient_merchant_id: payload.recipientMerchantId,
    amount_usd_cents: payload.amountUsdCents,
    totp_code: payload.totpCode,
  };
  if (payload.note) body.note = payload.note;
  const raw = await apiClient.post<RawTransferResponse>(
    `${TRANSFERS_BASE}`,
    body,
    { idempotencyKey: opts?.idempotencyKey ?? newIdempotencyKey() },
  );
  return {
    txId: raw.id,
    amountUsdCents: raw.amount_usd_cents,
    senderBalanceAfterCents: raw.sender_balance_after,
    recipientBalanceAfterCents: raw.recipient_balance_after,
    createdAt: raw.created_at,
  };
}

// --- Task 30: Withdraw + Bank accounts ----------------------------------

/**
 * GET /api/v1/wallet/bank-accounts — verified withdrawal destinations
 * the merchant has previously added (KYC-gated). 404 / NETWORK_ERROR
 * fallback returns `[]` so the Withdraw page renders an "Add bank
 * account" empty state instead of crashing.
 */
export async function listBankAccounts(): Promise<BankAccount[]> {
  try {
    const data = await apiClient.get<
      BankAccount[] | { bankAccounts?: BankAccount[] }
    >(`${BASE}/bank-accounts`);
    if (Array.isArray(data)) return data;
    if (
      data &&
      typeof data === 'object' &&
      'bankAccounts' in data &&
      Array.isArray((data as { bankAccounts?: BankAccount[] }).bankAccounts)
    ) {
      return (data as { bankAccounts: BankAccount[] }).bankAccounts;
    }
    return [];
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return [];
    }
    throw err;
  }
}

/**
 * POST /api/v1/wallet/withdraw — request a payout to a verified bank
 * account. Idempotency-Key + step-up 2FA token (set on the active
 * session by `verify2FA`) protect against duplicate withdrawals.
 */
export async function withdrawFunds(
  payload: WithdrawPayload,
  opts?: { idempotencyKey?: string },
): Promise<WithdrawResponse> {
  return apiClient.post<WithdrawResponse>(`${BASE}/withdraw`, payload, {
    idempotencyKey: opts?.idempotencyKey ?? newIdempotencyKey(),
  });
}

// --- Task 30: Transfer between own linked accounts ----------------------

/**
 * POST /api/v1/wallet/transfer — move funds between two linked
 * accounts the merchant owns (e.g. PayPal balance → main wallet).
 * Idempotency-Key keeps the operation safe under retry.
 */
export async function transferFunds(
  payload: TransferPayload,
  opts?: { idempotencyKey?: string },
): Promise<TransferResponse> {
  return apiClient.post<TransferResponse>(`${BASE}/transfer`, payload, {
    idempotencyKey: opts?.idempotencyKey ?? newIdempotencyKey(),
  });
}

// --- Task 30: 2FA verify (re-used by Send + Withdraw + Transfer) --------

/**
 * POST /api/v1/auth/2fa/challenge — submits the 6-digit TOTP code to
 * mint a short-lived fresh-perm proof (Phase 6 Wave 15a Task 53
 * canonical endpoint). The BE bumps Redis `2fa-fresh:{userId}`
 * timestamp on success so the subsequent Tier-A mutation passes
 * `requireFresh2FA`. Returned `token` (mapped from BE `fresh_perm_token`)
 * is forwarded by the caller as `X-2FA-Token` for audit-trail tagging.
 *
 * Phase 6 Wave 15a Task 52: rewired from the legacy
 * `/auth/2fa/verify` path (never registered on the BE — 404 surfaced
 * via test.skip(true) on) to the canonical
 * `/auth/2fa/challenge` endpoint that exists post-Task-53.
 *
 * vg-binding: P6.D-254
 * vg-binding: PLAN:task-52
 */
export async function verify2FA(code: string): Promise<Verify2FAResponse> {
  const resp = await apiClient.post<{
    fresh_perm_token: string;
    expires_at: string;
  }>(`/api/v1/auth/2fa/challenge`, { code });
  // Adapt to the legacy FE response shape (`{ ok, token }`) so existing
  // callers (WithdrawModal / SendBalanceModal / AddBankAccountModal /
  // InstantTopupModal) don't need to change.
  return { ok: true, token: resp.fresh_perm_token };
}

// --- Task 31: Linked accounts CRUD --------------------------------------

/** Shape returned by `POST /wallet/linked-accounts`. The BE wraps the
 *  newly-created link in `{ ok, linkedAccount }` so the FE can refetch
 *  the list cache + show the new row inline. */
export interface CreateLinkedAccountResponse {
  ok: true;
  linkedAccount: LinkedAccount;
}

/** Shape returned by `POST /wallet/bank-accounts`. */
export interface CreateBankAccountResponse {
  ok: true;
  bankAccount: BankAccount;
}

/** Payload for `POST /wallet/bank-accounts` — the form fields the
 *  Add Bank Account modal collects. The 2FA token is forwarded as the
 *  `X-2FA-Token` header rather than a body field so the security
 *  middleware on the BE can short-circuit unauthenticated requests. */
export interface CreateBankAccountPayload {
  bankName: string;
  accountName: string;
  /** Full account number — BE persists masked + last4 derived. */
  accountNumber: string;
  /** 'checking' | 'savings' — required by BE for US accounts. */
  accountType?: 'checking' | 'savings';
  /** Optional US routing number. */
  routingNumber?: string;
  /** ISO-3166 country code. */
  country: string;
  /** Optional SWIFT/BIC for international wires. */
  swift?: string;
  /** Optional IBAN for European accounts. */
  iban?: string;
  /** Structured merchant address for the payout destination. */
  merchantAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
}

/**
 * GET /api/v1/wallet/linked-accounts — list of Printway merchants the
 * current user has linked for fast Send Balance transfers.
 * Falls back to `[]` on 404 / NETWORK_ERROR so the page renders the
 * empty state instead of crashing.
 */
export async function listLinkedAccounts(): Promise<LinkedAccount[]> {
  try {
    const data = await apiClient.get<
      LinkedAccount[] | { linkedAccounts?: LinkedAccount[] }
    >(`${BASE}/linked-accounts`);
    if (Array.isArray(data)) return data;
    if (
      data &&
      typeof data === 'object' &&
      'linkedAccounts' in data &&
      Array.isArray(
        (data as { linkedAccounts?: LinkedAccount[] }).linkedAccounts,
      )
    ) {
      return (data as { linkedAccounts: LinkedAccount[] }).linkedAccounts;
    }
    return [];
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return [];
    }
    throw err;
  }
}

/**
 * POST /api/v1/wallet/linked-accounts — link a Printway merchant by
 * email. The BE validates the recipient exists + blocks self-links;
 * the FE additionally short-circuits self-links before the request.
 */
export async function createLinkedAccount(
  recipientEmail: string,
): Promise<CreateLinkedAccountResponse> {
  return apiClient.post<CreateLinkedAccountResponse>(
    `${BASE}/linked-accounts`,
    { recipientEmail },
  );
}

/**
 * DELETE /api/v1/wallet/linked-accounts/:id — remove a linked account.
 */
export async function deleteLinkedAccount(
  id: string,
): Promise<{ ok: true }> {
  return apiClient.delete<{ ok: true }>(
    `${BASE}/linked-accounts/${encodeURIComponent(id)}`,
  );
}

// --- Task 31: Bank accounts CRUD ----------------------------------------

/**
 * POST /api/v1/wallet/bank-accounts — register a new withdrawal
 * destination. The 2FA step-up token (returned by `verify2FA()`) is
 * forwarded as `X-2FA-Token` so the BE can bind the new account to
 * the elevated session.
 */
export async function createBankAccount(
  payload: CreateBankAccountPayload,
  twoFAToken: string,
): Promise<CreateBankAccountResponse> {
  return apiClient.post<CreateBankAccountResponse>(
    `${BASE}/bank-accounts`,
    payload,
    {
      headers: { 'X-2FA-Token': twoFAToken },
    },
  );
}

/**
 * DELETE /api/v1/wallet/bank-accounts/:id — remove a verified bank
 * account from the merchant's withdrawal destinations.
 */
export async function deleteBankAccount(
  id: string,
): Promise<{ ok: true }> {
  return apiClient.delete<{ ok: true }>(
    `${BASE}/bank-accounts/${encodeURIComponent(id)}`,
  );
}
